import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { toast } from 'react-hot-toast';
import { LoadingSpinner, PencilIcon } from '@/components/ui';
import { useAuthStore } from '@/store/authStore';
import type { UserProfileUpdate, EmailChangeRequest, AuthInfoResponse } from '@/types/auth';

// Industry options
const INDUSTRY_OPTIONS = [
  { value: '', label: 'Select an industry (optional)' },
  { value: '11 - Agriculture, Forestry, Fishing and Hunting', label: 'Agriculture & Forestry' },
  { value: '21 - Mining, Quarrying, and Oil and Gas Extraction', label: 'Mining & Oil/Gas' },
  { value: '22 - Utilities', label: 'Utilities' },
  { value: '23 - Construction', label: 'Construction' },
  { value: '31-33 - Manufacturing', label: 'Manufacturing' },
  { value: '42 - Wholesale Trade', label: 'Wholesale Trade' },
  { value: '44-45 - Retail Trade', label: 'Retail Trade' },
  { value: '48-49 - Transportation and Warehousing', label: 'Transportation & Warehousing' },
  { value: '51 - Information', label: 'Information & Technology' },
  { value: '52 - Finance and Insurance', label: 'Finance & Insurance' },
  { value: '53 - Real Estate and Rental and Leasing', label: 'Real Estate' },
  { value: '54 - Professional, Scientific, and Technical Services', label: 'Professional Services' },
  { value: '55 - Management of Companies and Enterprises', label: 'Management & Consulting' },
  { value: '56 - Administrative and Support and Waste Management and Remediation Services', label: 'Administrative & Support Services' },
  { value: '61 - Educational Services', label: 'Education' },
  { value: '62 - Health Care and Social Assistance', label: 'Healthcare & Social Services' },
  { value: '71 - Arts, Entertainment, and Recreation', label: 'Arts & Entertainment' },
  { value: '72 - Accommodation and Food Services', label: 'Hospitality & Food Services' },
  { value: '81 - Other Services (except Public Administration)', label: 'Other Services' },
  { value: '92 - Public Administration', label: 'Public Administration' },
];

// US States
const US_STATE_OPTIONS = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

// Country options
const COUNTRY_OPTIONS = [
  { value: '', label: 'Select Country' },
  { value: 'AU', label: 'Australia' },
  { value: 'CA', label: 'Canada' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'IN', label: 'India' },
  { value: 'ID', label: 'Indonesia' },
  { value: 'JP', label: 'Japan' },
  { value: 'MY', label: 'Malaysia' },
  { value: 'MX', label: 'Mexico' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'PH', label: 'Philippines' },
  { value: 'SG', label: 'Singapore' },
  { value: 'KR', label: 'South Korea' },
  { value: 'TW', label: 'Taiwan' },
  { value: 'TH', label: 'Thailand' },
  { value: 'US', label: 'United States' },
  { value: 'VN', label: 'Vietnam' },
];

// Profile form schema
const profileSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  company_name: z.string().min(1, 'Company name is required'),
  company_industry: z.string().optional(),
  phone: z.string().optional(),
  website: z.union([z.string().url('Please enter a valid URL'), z.literal('')]).optional(),
  address_line1: z.string().optional(),
  address_line2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postal_code: z.string().optional(),
  country: z.string().optional(),
});

type ProfileFormData = z.infer<typeof profileSchema>;
type EditingSection = 'personal' | 'company' | 'address' | null;

// Email change schema
const emailChangeSchema = z.object({
  new_email: z.string().email('Please enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type EmailChangeFormData = z.infer<typeof emailChangeSchema>;

export function ProfileSettings() {
  const queryClient = useQueryClient();
  const refreshUserData = useAuthStore((state) => state.refreshUserData);
  const [editingSection, setEditingSection] = useState<EditingSection>(null);
  const [showEmailDialog, setShowEmailDialog] = useState(false);

  // Fetch user profile
  const { data: profile, isLoading, error } = useQuery({
    queryKey: ['userProfile'],
    queryFn: () => authApi.getUserProfile(),
  });

  // Fetch auth info to check if Google is linked
  const { data: authInfo } = useQuery<AuthInfoResponse>({
    queryKey: ['auth-info'],
    queryFn: () => authApi.getAuthInfo(),
  });

  const hasGoogleLinked = authInfo?.google_linked ?? false;

  // Email change form
  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    reset: resetEmail,
    formState: { errors: emailErrors },
  } = useForm<EmailChangeFormData>({
    resolver: zodResolver(emailChangeSchema),
  });

  // Email change mutation
  const emailChangeMutation = useMutation({
    mutationFn: (data: EmailChangeRequest) => authApi.changeEmail(data),
    onSuccess: (response) => {
      toast.success(response.message);
      setShowEmailDialog(false);
      resetEmail();
      // No longer log out - the current email remains active until the new one is verified
      // User can continue using the app while they verify the new email
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to request email change');
    },
  });

  const onEmailSubmit = (data: EmailChangeFormData) => {
    emailChangeMutation.mutate(data);
  };

  const handleCancelEmailChange = () => {
    setShowEmailDialog(false);
    resetEmail();
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  });

  const selectedCountry = watch('country');

  // Reset form when profile data is loaded or editing section changes
  useEffect(() => {
    if (profile) {
      reset({
        first_name: profile.firstName || '',
        last_name: profile.lastName || '',
        company_name: profile.companyName || '',
        company_industry: profile.companyIndustry || '',
        phone: profile.phone || '',
        website: profile.website || '',
        address_line1: profile.companyAddress?.address_line1 || '',
        address_line2: profile.companyAddress?.address_line2 || '',
        city: profile.companyAddress?.city || '',
        state: profile.companyAddress?.state || '',
        postal_code: profile.companyAddress?.postal_code || '',
        country: profile.companyAddress?.country || '',
      });
    }
  }, [profile, reset, editingSection]);

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: (data: UserProfileUpdate) => authApi.updateUserProfile(data),
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      // Also refresh the auth store's user data so the dropdown and banner update
      await refreshUserData();
      toast.success('Profile updated successfully');
      setEditingSection(null);
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to update profile');
    },
  });

  const onSubmit = (data: ProfileFormData) => {
    // Check if any address fields have values
    const hasAddressData = Boolean(
      data.address_line1 || data.address_line2 || data.city ||
      data.state || data.postal_code || data.country
    );

    const updateData: UserProfileUpdate = {
      first_name: data.first_name,
      last_name: data.last_name,
      company_name: data.company_name,
      company_industry: data.company_industry || null,
      phone: data.phone || null,
      website: data.website || null,
      company_address: hasAddressData ? {
        address_line1: data.address_line1 || null,
        address_line2: data.address_line2 || null,
        city: data.city || null,
        state: data.state || null,
        postal_code: data.postal_code || null,
        country: data.country || null,
      } : null,
    };

    updateMutation.mutate(updateData);
  };

  const handleCancel = () => {
    setEditingSection(null);
    // Reset form to original values
    if (profile) {
      reset({
        first_name: profile.firstName || '',
        last_name: profile.lastName || '',
        company_name: profile.companyName || '',
        company_industry: profile.companyIndustry || '',
        phone: profile.phone || '',
        website: profile.website || '',
        address_line1: profile.companyAddress?.address_line1 || '',
        address_line2: profile.companyAddress?.address_line2 || '',
        city: profile.companyAddress?.city || '',
        state: profile.companyAddress?.state || '',
        postal_code: profile.companyAddress?.postal_code || '',
        country: profile.companyAddress?.country || '',
      });
    }
  };

  const getIndustryLabel = (value: string) => {
    const option = INDUSTRY_OPTIONS.find(opt => opt.value === value);
    return option?.label || value || 'Not specified';
  };

  const getCountryLabel = (value: string) => {
    const option = COUNTRY_OPTIONS.find(opt => opt.value === value);
    return option?.label || value || '';
  };

  const getStateLabel = (value: string, country: string) => {
    if (country === 'US') {
      const option = US_STATE_OPTIONS.find(opt => opt.value === value);
      return option?.label || value || '';
    }
    return value || '';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" variant="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load profile. Please try again.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Profile Settings</h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Personal Information */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Personal Information</h3>
            {editingSection !== 'personal' && (
              <button
                type="button"
                onClick={() => setEditingSection('personal')}
                className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-md hover:bg-gray-100"
                title="Edit personal information"
              >
                <PencilIcon />
              </button>
            )}
          </div>

          {editingSection === 'personal' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    {...register('first_name')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.first_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.first_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.first_name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    {...register('last_name')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.last_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.last_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-900 transition-colors disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">First Name</p>
                <p className="text-gray-900">{profile?.firstName || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Last Name</p>
                <p className="text-gray-900">{profile?.lastName || 'Not specified'}</p>
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center gap-1">
                  <p className="text-sm text-gray-500">Email Address</p>
                  <button
                    type="button"
                    onClick={() => setShowEmailDialog(true)}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors rounded-md hover:bg-gray-100"
                    title="Change email address"
                  >
                    <PencilIcon className="w-3 h-3" />
                  </button>
                </div>
                <p className="text-gray-900">{profile?.email}</p>
              </div>
            </div>
          )}
        </div>

        {/* Company Information */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Company Information</h3>
            {editingSection !== 'company' && (
              <button
                type="button"
                onClick={() => setEditingSection('company')}
                className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-md hover:bg-gray-100"
                title="Edit company information"
              >
                <PencilIcon />
              </button>
            )}
          </div>

          {editingSection === 'company' ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company_name"
                    {...register('company_name')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.company_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.company_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.company_name.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="company_industry" className="block text-sm font-medium text-gray-700 mb-1">
                    Industry
                  </label>
                  <select
                    id="company_industry"
                    {...register('company_industry')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {INDUSTRY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    {...register('phone')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    {...register('website')}
                    placeholder="https://example.com"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      errors.website ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.website && (
                    <p className="mt-1 text-sm text-red-600">{errors.website.message}</p>
                  )}
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-900 transition-colors disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Company Name</p>
                <p className="text-gray-900">{profile?.companyName || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Industry</p>
                <p className="text-gray-900">{getIndustryLabel(profile?.companyIndustry || '')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-gray-900">{profile?.phone || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Website</p>
                <p className="text-gray-900">
                  {profile?.website ? (
                    <a href={profile.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {profile.website}
                    </a>
                  ) : (
                    'Not specified'
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Company Address */}
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Company Address</h3>
            {editingSection !== 'address' && (
              <button
                type="button"
                onClick={() => setEditingSection('address')}
                className="p-2 text-gray-400 hover:text-blue-600 transition-colors rounded-md hover:bg-gray-100"
                title="Edit company address"
              >
                <PencilIcon />
              </button>
            )}
          </div>

          {editingSection === 'address' ? (
            <>
              <div className="space-y-4">
                <div>
                  <label htmlFor="address_line1" className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    id="address_line1"
                    {...register('address_line1')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="address_line2" className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    id="address_line2"
                    {...register('address_line2')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      id="city"
                      {...register('city')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-1">
                      State/Province
                    </label>
                    {selectedCountry === 'US' ? (
                      <select
                        id="state"
                        {...register('state')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {US_STATE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        id="state"
                        {...register('state')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    )}
                  </div>

                  <div>
                    <label htmlFor="postal_code" className="block text-sm font-medium text-gray-700 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      id="postal_code"
                      {...register('postal_code')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <select
                    id="country"
                    {...register('country')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {COUNTRY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="px-4 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-900 transition-colors disabled:opacity-50"
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              {profile?.companyAddress?.address_line1 || profile?.companyAddress?.city ? (
                <>
                  {profile?.companyAddress?.address_line1 && (
                    <p className="text-gray-900">{profile.companyAddress.address_line1}</p>
                  )}
                  {profile?.companyAddress?.address_line2 && (
                    <p className="text-gray-900">{profile.companyAddress.address_line2}</p>
                  )}
                  <p className="text-gray-900">
                    {[
                      profile?.companyAddress?.city,
                      getStateLabel(profile?.companyAddress?.state || '', profile?.companyAddress?.country || ''),
                      profile?.companyAddress?.postal_code,
                    ].filter(Boolean).join(', ')}
                  </p>
                  {profile?.companyAddress?.country && (
                    <p className="text-gray-900">{getCountryLabel(profile.companyAddress.country)}</p>
                  )}
                </>
              ) : (
                <p className="text-gray-500">No address specified</p>
              )}
            </div>
          )}
        </div>
      </form>

      {/* Email Change Dialog */}
      {showEmailDialog && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCancelEmailChange}
            />

            {/* Dialog */}
            <div className="relative inline-block w-full max-w-md p-6 my-8 text-left align-middle bg-white rounded-lg shadow-xl transform transition-all">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Change Email Address
              </h3>

              <p className="text-sm text-gray-600 mb-4">
                A verification email will be sent to your new address. Your current email will remain active until you verify the new one.
              </p>

              {hasGoogleLinked && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-sm text-amber-800">
                    <strong>Warning:</strong> Your Google account is currently linked. Changing your email will unlink Google sign-in, and you'll need to sign in with your password.
                  </p>
                </div>
              )}

              <form onSubmit={handleEmailSubmit(onEmailSubmit)} className="space-y-4">
                <div>
                  <label htmlFor="current_email" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Email
                  </label>
                  <input
                    type="email"
                    id="current_email"
                    value={profile?.email || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500"
                  />
                </div>

                <div>
                  <label htmlFor="new_email" className="block text-sm font-medium text-gray-700 mb-1">
                    New Email *
                  </label>
                  <input
                    type="email"
                    id="new_email"
                    {...registerEmail('new_email')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      emailErrors.new_email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your new email address"
                  />
                  {emailErrors.new_email && (
                    <p className="mt-1 text-sm text-red-600">{emailErrors.new_email.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email_password" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    id="email_password"
                    {...registerEmail('password')}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      emailErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Enter your current password"
                  />
                  {emailErrors.password && (
                    <p className="mt-1 text-sm text-red-600">{emailErrors.password.message}</p>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCancelEmailChange}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={emailChangeMutation.isPending}
                    className="px-4 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-900 transition-colors disabled:opacity-50"
                  >
                    {emailChangeMutation.isPending ? 'Changing...' : 'Change Email'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
